import React, { useEffect } from 'react';
import { features } from 'configs';

import { storeData, logoutFromPortal } from 'utils/AuxiliarFunctions';

import {
  faUserGroup,
  faUser,
  faUserDoctor,
  faShare,
  faFolderOpen,
  faKey,
  faClockRotateLeft,
  faRightFromBracket,
  faHouse,
  faFileMedical,
  faFlask,
  faBookMedical,
  faTelevision,
  faLineChart,
  faCogs,
  faCalendar,
} from '@fortawesome/free-solid-svg-icons';
import { HomePage } from 'pages/HomePage';
import { EHRPage } from 'pages/EHRPage';
import SearchPage from 'pages/SearchPage/SearchPage';

import { SharePage } from 'pages/SharePage';
import { AccessHistoryPage } from 'pages/AcessHistoryPage';
import { useEHRContext } from 'contexts/EHRContext';

import { TeleconsultingPage } from 'pages/TeleconsultingPage';
import { IntegratedDocumentsPage } from 'pages/IntegratedDocumentsPage';
import { ReprocessingPage } from 'pages/ReprocessingPage';
import { Scheduling } from 'pages/Scheduling';
import ProfilePage from 'pages/ProfilePage/ProfilePage';
import PrivacyPage from 'pages/PrivacyPage/PrivacyPage';

export function Pages(roles) {
  return [
    {
      id: 'home',
      active: true,
      name: 'Início',
      icon: faHouse,
      title: 'Início',
      path: '/home',
      onAccess: async () => {},
      component: () => <HomePage />,
      doesNotAppearOnHome: true,
      doesNotAppearOnSidebar: true,
    },
    {
      id: 'documentosintegrados',
      active: roles?.isProfessional,
      name: 'Pesquisa de documentos integrados',
      icon: faLineChart,
      description:
        'Reprocesse documentos clinicos com erro de integração no HU Digital',
      title: 'Pesquisa de documentos integrados',
      path: '/documentosintegrados',
      onAccess: async () => {},
      component: () => <IntegratedDocumentsPage />,

      practitionerIndex: 2,
    },
    {
      id: 'processamento',
      active: roles?.isProfessional,
      name: 'Monitoramento de reprocessamento',
      description:
        'Visualize o log de processamento dos documentos clinicos integrados no HU Digital',
      icon: faCogs,
      title: 'Monitoramento de reprocessamento',
      path: '/processamento',
      onAccess: async () => {},
      component: () => <ReprocessingPage />,

      practitionerIndex: 3,
    },
    {
      id: 'historico',
      active: !!roles,
      title: 'Histórico de acessos',
      icon: faClockRotateLeft,
      name: 'Histórico de acessos',
      path: '/historico',
      doesNotAppearOnHome: true,
      doesNotAppearOnSidebar: true,
      onAccess: async () => {},
      component: () => <AccessHistoryPage />,
    },
    {
      id: 'registros',
      active: true,
      name: 'Registros',
      icon: faBookMedical,
      title: 'Registros',
      path: '/registros',
      onAccess: async () => {},
      component: () => <EHRPage />,
      doesNotAppearOnHome: true,
      doesNotAppearOnSidebar: true,
      normalIndex: 99,
      practitionerIndex: 99,
    },
    {
      id: 'scheduling',
      active: !roles?.isProfessional,
      name: 'Minha agenda',
      icon: faCalendar,
      title: 'Minha agenda',
      path: '/scheduling',
      description: 'Acesse seus agendamentos atuais e realizados',
      normalIndex: 0,
      onAccess: async () => {},
      component: () => <Scheduling />,
    },
    {
      id: 'resultadosdeexames',
      active: !roles?.isProfessional,
      name: 'Resultados de exames',
      icon: faFlask,
      title: 'Resultados de exames',
      path: '/resultadosdeexames',
      description: 'Acesse seus resultados laboratoriais e de imagem',
      normalIndex: 2,
      onAccess: async () => {},
      component: function EncapsulatedEHRPage() {
        const { setLoggedUserAsPatient } = useEHRContext();
        useEffect(() => {
          setLoggedUserAsPatient();
        }, []);
        return <EHRPage page="Results" />;
      },
    },
    {
      id: 'documentos',
      active: !roles?.isProfessional,
      name: 'Atestados prescrições e outros',
      icon: faFileMedical,
      title: 'Atestados prescrições e outros',
      path: '/documentos',
      description: 'Acesse suas prescrições de exames e procedimentos',
      normalIndex: 3,
      onAccess: async () => {},
      component: function EncapsulatedEHRPage() {
        const { setLoggedUserAsPatient } = useEHRContext();
        useEffect(() => {
          setLoggedUserAsPatient();
        }, []);
        return <EHRPage page="Documents" />;
      },
    },
    {
      id: 'outrosregistros',
      active: roles?.isProfessional,
      name: 'Outros registros',
      icon: faFolderOpen,
      title: 'Outros registros',
      path: '/outrosregistros',
      description: 'Acesse outros tipos de documentos clínicos',
      normalIndex: 4,
      onAccess: async () => {},
      component: function EncapsulatedEHRPage() {
        const { setLoggedUserAsPatient } = useEHRContext();
        useEffect(() => {
          setLoggedUserAsPatient();
        }, []);
        return <EHRPage page="EHR" />;
      },
    },
    {
      id: 'search',
      active: roles?.isProfessional,
      name: 'Pesquisar pacientes',
      icon: faUserGroup,
      title: 'Pesquisar pacientes',
      path: '/search',
      onAccess: async () => {},
      component: () => <SearchPage />,
      practitionerIndex: 0,
    },
    {
      id: 'share',
      active: true,
      name: 'Compartilhamentos',
      icon: faShare,
      title: 'Compartilhamentos',
      path: '/share',
      description: 'Gerencie os consentimentos de acesso aos seus registros',
      normalIndex: 1,
      practitionerIndex: 1,
      onAccess: async () => {},
      component: () => <SharePage />,
    },
    {
      id: 'teleatendimentos',
      active: !roles?.isProfessional,
      name: 'Teleatendimentos',
      icon: faTelevision,
      title: 'Teleatendimentos',
      path: '/teleatendimentos',
      description: 'Acesse suas teleconsultas agendadas e realizadas',
      normalIndex: 3,
      practitionerIndex: 99,
      onAccess: async () => {},
      component: () => <TeleconsultingPage />,
    },
    {
      id: 'perfil',
      active: features.profile,
      name: 'pages-titlePages-titleProfile',
      icon: roles ? faUserDoctor : faUser,
      title: 'pages-titlePages-titleProfile',
      path: '/perfil',
      doesNotAppearOnHome: !!roles,
      doesNotAppearOnSidebar: !!roles,

      onAccess: async () => {
        await storeData('purposeOfUse', '');
      },
      component: () => <ProfilePage />,
    },

    {
      id: 'privacidade',
      active: !!roles,
      name: 'pages-titlePages-namePrivacy',
      icon: faKey,
      title: 'pages-titlePages-titlePrivacy',
      path: '/privacidade',
      doesNotAppearOnHome: true,
      doesNotAppearOnSidebar: true,
      onAccess: async () => {
        await storeData('purposeOfUse', '');
      },
      component: () => <PrivacyPage />,
    },
    {
      id: 'logout',
      active: true,
      name: 'pages-titlePages-nameLogout',
      title: 'pages-titlePages-nameLogout',
      icon: faRightFromBracket,
      path: '/logout',

      doesNotAppearOnHome: true,
      doesNotAppearOnSidebar: true,
      component: function LogoutPage() {
        useEffect(() => {
          async function asyncCalls() {
            await logoutFromPortal();
          }
          asyncCalls();
          window.location.assign(
            `https://${process.env.REACT_APP_ENDPOINT_principalDomain}`,
          );
        }, []);
        return <></>;
      },
    },
  ];
}

export const initialPageId = 'home';
